import React, { useState, useEffect } from 'react';
import { 
  Card, 
  CardContent, 
  CardHeader, 
  CardTitle 
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { 
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger
} from '@/components/ui/alert-dialog';
import { 
  Search,
  Filter,
  RefreshCw,
  CheckCheck,
  X,
  Loader2,
  Images 
} from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import PhotoReviewCard from './PhotoReviewCard';

interface PhotoData {
  id: string;
  practitioner_id: string;
  photo_url: string;
  photo_description: string | null;
  display_order: number;
  is_active: boolean;
  photo_type: string;
  admin_review_status: string;
  admin_notes: string | null;
  reviewed_by: string | null;
  reviewed_at: string | null;
  created_at: string;
  updated_at: string;
  practitioner?: {
    first_name: string | null;
    last_name: string | null;
    professional_title: string | null;
  };
}

const AdminPractitionerGallery: React.FC = () => {
  const { toast } = useToast();
  const [photos, setPhotos] = useState<PhotoData[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [isBatchProcessing, setIsBatchProcessing] = useState(false);
  const [selectedPhotos, setSelectedPhotos] = useState<Set<string>>(new Set());

  useEffect(() => {
    fetchPhotos();
  }, [searchQuery, statusFilter]);

  const fetchPhotos = async () => {
    setIsLoading(true);
    try {
      let query = supabase
        .from('practitioner_photos')
        .select(`
          *,
          practitioner:practitioners(
            first_name,
            last_name,
            professional_title
          )
        `)
        .eq('is_active', true)
        .order('created_at', { ascending: false });

      // Filter by status - now show approved photos by default with flagged highlighted
      if (statusFilter !== 'all') {
        query = query.eq('admin_review_status', statusFilter);
      } else {
        // By default, show approved photos (the main gallery) with flagged photos highlighted
        query = query.in('admin_review_status', ['approved', 'flagged']);
      }

      const { data, error } = await query;

      if (error) throw error;

      let filteredData = data || [];

      // Apply search filter
      if (searchQuery.trim()) {
        const searchLower = searchQuery.toLowerCase();
        filteredData = filteredData.filter(photo => {
          const practitionerName = photo.practitioner 
            ? `${photo.practitioner.first_name || ''} ${photo.practitioner.last_name || ''}`.toLowerCase()
            : '';
          const description = photo.photo_description?.toLowerCase() || '';
          
          return practitionerName.includes(searchLower) || 
                 description.includes(searchLower);
        });
      }

      setPhotos(filteredData);
    } catch (error) {
      console.error('Error fetching photos:', error);
      toast({
        title: "Error",
        description: "Failed to fetch photos. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleBatchAction = async (action: 'flag' | 'unflag' | 'remove') => {
    if (selectedPhotos.size === 0) {
      toast({
        title: "No Selection",
        description: "Please select photos to perform batch action.",
        variant: "destructive"
      });
      return;
    }

    setIsBatchProcessing(true);
    
    try {
      let updateData: any = {};
      
      if (action === 'flag') {
        updateData = { admin_review_status: 'flagged', reviewed_at: new Date().toISOString() };
      } else if (action === 'unflag') {
        updateData = { admin_review_status: 'approved', reviewed_at: new Date().toISOString() };
      } else if (action === 'remove') {
        updateData = { admin_review_status: 'rejected', is_active: false, reviewed_at: new Date().toISOString() };
      }

      const { error } = await supabase
        .from('practitioner_photos')
        .update(updateData)
        .in('id', Array.from(selectedPhotos));

      if (error) throw error;

      toast({
        title: "Batch Action Completed",
        description: `${selectedPhotos.size} photo(s) ${action === 'flag' ? 'flagged for review' : action === 'unflag' ? 'restored to gallery' : 'removed from gallery'} successfully.`,
      });

      setSelectedPhotos(new Set());
      fetchPhotos();
    } catch (error) {
      console.error('Error performing batch action:', error);
      toast({
        title: "Error",
        description: "Failed to perform batch action. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsBatchProcessing(false);
    }
  };

  const handleSelectPhoto = (photoId: string, selected: boolean) => {
    const newSelection = new Set(selectedPhotos);
    if (selected) {
      newSelection.add(photoId);
    } else {
      newSelection.delete(photoId);
    }
    setSelectedPhotos(newSelection);
  };

  const handleSelectAll = () => {
    if (selectedPhotos.size === photos.length) {
      setSelectedPhotos(new Set());
    } else {
      setSelectedPhotos(new Set(photos.map(photo => photo.id)));
    }
  };

  const getStatusCounts = () => {
    const counts = {
      flagged: photos.filter(p => p.admin_review_status === 'flagged').length,
      approved: photos.filter(p => p.admin_review_status === 'approved').length,
      rejected: photos.filter(p => p.admin_review_status === 'rejected').length,
    };
    return counts;
  };

  const statusCounts = getStatusCounts();

  return (
    <div className="space-y-6">
      {/* Search and Filters - Simple Layout */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
          <Input
            placeholder="Search by practitioner name or description..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
          />
        </div>
        
        <div className="sm:w-48">
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger>
              <Filter className="h-4 w-4 mr-2" />
              <SelectValue placeholder="Filter by status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Photos</SelectItem>
              <SelectItem value="approved">Live in Gallery</SelectItem>
              <SelectItem value="flagged">Flagged for Review</SelectItem>
              <SelectItem value="rejected">Removed</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <Button 
          variant="outline" 
          size="sm" 
          onClick={fetchPhotos}
          disabled={isLoading}
        >
          <RefreshCw className={`h-4 w-4 mr-1 ${isLoading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Batch Actions */}
      {selectedPhotos.size > 0 && (
        <div className="flex items-center justify-between p-4 bg-muted/50 rounded-lg border">
          <div className="flex items-center gap-2">
            <Badge variant="secondary">{selectedPhotos.size} selected</Badge>
            <Button 
              variant="outline" 
              size="sm"
              onClick={handleSelectAll}
            >
              {selectedPhotos.size === photos.length ? 'Deselect All' : 'Select All'}
            </Button>
          </div>
          
          <div className="flex gap-2">
            <Button 
              size="sm"
              disabled={isBatchProcessing}
              onClick={() => handleBatchAction('unflag')}
            >
              <CheckCheck className="h-4 w-4 mr-1" />
              Approve
            </Button>

            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button 
                  size="sm" 
                  variant="destructive"
                  disabled={isBatchProcessing}
                >
                  <X className="h-4 w-4 mr-1" />
                  Remove
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Remove Selected Photos</AlertDialogTitle>
                  <AlertDialogDescription>
                    Are you sure you want to remove {selectedPhotos.size} selected photos? 
                    They will no longer be visible to users.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction 
                    onClick={() => handleBatchAction('remove')}
                    className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                  >
                    Remove {selectedPhotos.size} Photos
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        </div>
      )}

      {/* Photos Grid */}
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin" />
          <span className="ml-2">Loading photos...</span>
        </div>
      ) : photos.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Images className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <h3 className="text-lg font-medium mb-2">No photos found</h3>
            <p className="text-muted-foreground">
              {searchQuery || statusFilter !== 'all' 
                ? 'Try adjusting your filters to see more photos.'
                : 'No practitioner photos have been uploaded yet.'
              }
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {photos.map((photo) => (
            <div key={photo.id} className="relative">
              {/* Selection checkbox for batch actions */}
              <div className="absolute top-2 left-2 z-10">
                <input
                  type="checkbox"
                  checked={selectedPhotos.has(photo.id)}
                  onChange={(e) => handleSelectPhoto(photo.id, e.target.checked)}
                  className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
                />
              </div>
              
              <PhotoReviewCard 
                photo={photo} 
                onStatusUpdate={fetchPhotos}
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminPractitionerGallery;